import React, { useEffect, useRef, useState, useCallback } from 'react'
import {
  Box,
  VStack,
  HStack,
  Input,
  Text,
  Alert,
  AlertIcon,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  Flex,
  Spacer,
} from '@chakra-ui/react'
import { FiSearch, FiChevronUp, FiChevronDown, FiX } from 'react-icons/fi'
import Mark from 'mark.js'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface TextSearchViewerProps {
  text: string
  defaultSearchTerm?: string
}

export default function TextSearchViewer({ text, defaultSearchTerm = '' }: TextSearchViewerProps) {
  const textContainerRef = useRef<HTMLDivElement>(null)
  const markInstanceRef = useRef<Mark | null>(null)
  const [searchTerm, setSearchTerm] = useState(defaultSearchTerm)
  const [currentMatch, setCurrentMatch] = useState(0)
  const [totalMatches, setTotalMatches] = useState(0)
  const [isSearching, setIsSearching] = useState(false)
  const { t } = useAppTranslation('files')

  // Initialize Mark.js instance
  useEffect(() => {
    if (textContainerRef.current) {
      markInstanceRef.current = new Mark(textContainerRef.current)
    }
    return () => {
      if (markInstanceRef.current) {
        markInstanceRef.current.unmark()
      }
    }
  }, [])

  // Search function
  const performSearch = useCallback((term: string) => {
    if (!markInstanceRef.current || !term.trim()) {
      markInstanceRef.current?.unmark()
      setTotalMatches(0)
      setCurrentMatch(0)
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    markInstanceRef.current.unmark()

    markInstanceRef.current.mark(term, {
      className: 'text-highlight',
      done: (totalMarks) => {
        setTotalMatches(totalMarks)
        setCurrentMatch(totalMarks > 0 ? 1 : 0)
        setIsSearching(false)

        // Scroll to first match
        if (totalMarks > 0) {
          const firstMatch = textContainerRef.current?.querySelector('.text-highlight')
          if (firstMatch) {
            firstMatch.scrollIntoView({ behavior: 'smooth', block: 'center' })
            firstMatch.classList.add('text-highlight-current')
          }
        }
      },
    })
  }, [])

  // Navigate between matches
  const navigateMatch = useCallback(
    (direction: 'next' | 'prev') => {
      const matches = textContainerRef.current?.querySelectorAll('.text-highlight')
      if (!matches || matches.length === 0) return

      // Remove current highlight
      matches.forEach((match) => match.classList.remove('text-highlight-current'))

      let newIndex
      if (direction === 'next') {
        newIndex = currentMatch >= totalMatches ? 1 : currentMatch + 1
      } else {
        newIndex = currentMatch <= 1 ? totalMatches : currentMatch - 1
      }

      setCurrentMatch(newIndex)

      // Highlight and scroll to new match
      const targetMatch = matches[newIndex - 1]
      if (targetMatch) {
        targetMatch.classList.add('text-highlight-current')
        targetMatch.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
    [currentMatch, totalMatches]
  )

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
  }

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        performSearch(searchTerm)
      } else {
        markInstanceRef.current?.unmark()
        setTotalMatches(0)
        setCurrentMatch(0)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchTerm, performSearch])

  // Clear search
  const clearSearch = () => {
    setSearchTerm('')
    markInstanceRef.current?.unmark()
    setTotalMatches(0)
    setCurrentMatch(0)
  }

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (e.shiftKey) {
        navigateMatch('prev')
      } else {
        navigateMatch('next')
      }
    } else if (e.key === 'Escape') {
      clearSearch()
    }
  }

  // Initial search if defaultSearchTerm is provided
  useEffect(() => {
    if (defaultSearchTerm) {
      performSearch(defaultSearchTerm)
    }
  }, [defaultSearchTerm, performSearch])

  if (!text) {
    return (
      <Alert status="info">
        <AlertIcon />
        {t('textSearchViewer.noTextContent')}
      </Alert>
    )
  }

  return (
    <VStack spacing={4} align="stretch" h="full">
      {/* Search Controls */}
      <Flex gap={3} align="center">
        <InputGroup maxW="400px">
          <InputLeftElement pointerEvents="none">
            <FiSearch color="gray.400" size="16px" />
          </InputLeftElement>
          <Input
            placeholder={t('textSearchViewer.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            bg="gray.50"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="xl"
            boxShadow="sm"
            fontSize="sm"
            _focus={{
              borderColor: 'blue.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
              bg: 'white',
            }}
            _hover={{
              borderColor: 'gray.400',
            }}
            _placeholder={{
              color: 'gray.500',
              fontSize: 'sm',
            }}
            transition="all 0.2s"
          />
          {searchTerm && (
            <InputRightElement>
              <IconButton
                aria-label={t('textSearchViewer.clearSearch')}
                icon={<FiX />}
                size="xs"
                variant="ghost"
                onClick={clearSearch}
              />
            </InputRightElement>
          )}
        </InputGroup>

        {/* Match Navigation */}
        {totalMatches > 0 && (
          <HStack spacing={2}>
            <Text fontSize="sm" color="gray.600" minW="fit-content">
              {isSearching
                ? t('textSearchViewer.searching')
                : t('textSearchViewer.matchesCount', {
                    current: currentMatch,
                    total: totalMatches,
                  })}
            </Text>
            <HStack spacing={1}>
              <IconButton
                aria-label={t('textSearchViewer.previousMatch')}
                icon={<FiChevronUp />}
                size="sm"
                variant="outline"
                borderColor="gray.200"
                _hover={{
                  borderColor: 'blue.500',
                  color: 'blue.500',
                }}
                onClick={() => navigateMatch('prev')}
                isDisabled={totalMatches === 0}
              />
              <IconButton
                aria-label={t('textSearchViewer.nextMatch')}
                icon={<FiChevronDown />}
                size="sm"
                variant="outline"
                borderColor="gray.200"
                _hover={{
                  borderColor: 'blue.500',
                  color: 'blue.500',
                }}
                onClick={() => navigateMatch('next')}
                isDisabled={totalMatches === 0}
              />
            </HStack>
          </HStack>
        )}

        <Spacer />

        {/* Keyboard shortcuts hint */}
        <Text fontSize="xs" color="gray.500">
          {t('textSearchViewer.keyboardShortcuts')}
        </Text>
      </Flex>

      {/* Text Content */}
      <Box
        ref={textContainerRef}
        p={4}
        bg="gray.50"
        borderRadius="md"
        flex={1}
        minH={0}
        overflow="auto"
        whiteSpace="pre-wrap"
        fontFamily="monospace"
        fontSize="sm"
        className="ph-no-capture file-content-private"
        data-private="true"
        sx={{
          '.text-highlight': {
            backgroundColor: 'yellow.200',
            padding: '1px 2px',
            borderRadius: '2px',
          },
          '.text-highlight-current': {
            backgroundColor: 'orange.300',
            fontWeight: 'bold',
          },
        }}
      >
        {text}
      </Box>
    </VStack>
  )
}
